import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Copy, Trash2, Shield, Search, Users, Eye } from "lucide-react";
import { mockUserProfiles } from "@/data/mockUserProfiles";
import { UserProfile, PermissionKey, ALL_PERMISSIONS, PERMISSION_CATEGORIES } from "@/types/permissions";
import { toast } from "sonner";

interface ProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  formData: {
    name: string;
    description: string;
    permissions: PermissionKey[];
  };
  setFormData: React.Dispatch<React.SetStateAction<{
    name: string;
    description: string;
    permissions: PermissionKey[];
  }>>;
  onSave: () => void;
  togglePermission: (permission: PermissionKey) => void;
  toggleCategoryPermissions: (category: string, checked: boolean) => void;
  isCategoryFullySelected: (category: string) => boolean;
  isCategoryPartiallySelected: (category: string) => boolean;
}

const ProfileDialog = ({
  open,
  onOpenChange,
  title,
  formData,
  setFormData,
  onSave,
  togglePermission,
  toggleCategoryPermissions,
  isCategoryFullySelected,
  isCategoryPartiallySelected,
}: ProfileDialogProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-4xl h-[85vh] flex flex-col">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>Configure profile details and permissions</DialogDescription>
      </DialogHeader>

      <div className="flex-1 overflow-y-auto space-y-4 pr-2">
        <div className="grid gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Profile Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Warehouse Manager"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this profile's purpose"
              rows={2}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Permissions ({formData.permissions.length} selected)</Label>
          <div className="border rounded-md">
            <Tabs defaultValue={PERMISSION_CATEGORIES[0].id} className="w-full">
              <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0 flex-wrap h-auto">
                {PERMISSION_CATEGORIES.map((category) => (
                  <TabsTrigger
                    key={category.id}
                    value={category.id}
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary"
                  >
                    {category.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {PERMISSION_CATEGORIES.map((category) => {
                const categoryPerms = ALL_PERMISSIONS.filter((p) => p.category === category.id);
                const isFullySelected = isCategoryFullySelected(category.id);
                const isPartiallySelected = isCategoryPartiallySelected(category.id);

                return (
                  <TabsContent key={category.id} value={category.id} className="p-4 space-y-4 max-h-[300px] overflow-y-auto">
                    <div className="flex items-center space-x-2 pb-2 border-b sticky top-0 bg-background z-10">
                      <Checkbox
                        id={`category-${category.id}`}
                        checked={isFullySelected}
                        onCheckedChange={(checked) => toggleCategoryPermissions(category.id, checked as boolean)}
                        className={isPartiallySelected ? "data-[state=checked]:bg-primary/50" : ""}
                      />
                      <Label htmlFor={`category-${category.id}`} className="font-semibold cursor-pointer">
                        Select All {category.label}
                      </Label>
                    </div>

                    <div className="space-y-3">
                      {categoryPerms.map((permission) => (
                        <div key={permission.key} className="flex items-start space-x-2">
                          <Checkbox
                            id={permission.key}
                            checked={formData.permissions.includes(permission.key)}
                            onCheckedChange={() => togglePermission(permission.key)}
                          />
                          <div className="grid gap-1 leading-none">
                            <Label htmlFor={permission.key} className="cursor-pointer font-medium">
                              {permission.label}
                            </Label>
                            <p className="text-sm text-muted-foreground">{permission.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                );
              })}
            </Tabs>
          </div>
        </div>
      </div>

      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button onClick={onSave}>Save Profile</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export function UserProfilesPage() {
  const [profiles, setProfiles] = useState<UserProfile[]>(mockUserProfiles);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<UserProfile | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    permissions: [] as PermissionKey[],
  });

  const filteredProfiles = profiles.filter(
    (profile) =>
      profile.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      profile.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreate = () => {
    setFormData({ name: "", description: "", permissions: [] });
    setIsCreateDialogOpen(true);
  };

  const handleView = (profile: UserProfile) => {
    setSelectedProfile(profile);
    setFormData({
      name: profile.name,
      description: profile.description,
      permissions: profile.permissions,
    });
    setIsViewDialogOpen(true);
  };

  const handleEdit = (profile: UserProfile) => {
    if (profile.isSystem) {
      toast.error("System profiles cannot be edited");
      return;
    }
    setSelectedProfile(profile);
    setFormData({
      name: profile.name,
      description: profile.description,
      permissions: profile.permissions,
    });
    setIsEditDialogOpen(true);
  };

  const handleDuplicate = (profile: UserProfile) => {
    setFormData({
      name: `${profile.name} (Copy)`,
      description: profile.description,
      permissions: profile.permissions,
    });
    setIsCreateDialogOpen(true);
  };

  const handleDelete = (profile: UserProfile) => {
    if (profile.isSystem) {
      toast.error("System profiles cannot be deleted");
      return;
    }
    setSelectedProfile(profile);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedProfile) {
      setProfiles(profiles.filter((p) => p.id !== selectedProfile.id));
      toast.success("User profile deleted successfully");
      setIsDeleteDialogOpen(false);
      setSelectedProfile(null);
    }
  };

  const saveProfile = () => {
    if (!formData.name.trim()) {
      toast.error("Profile name is required");
      return;
    }

    if (formData.permissions.length === 0) {
      toast.error("At least one permission must be selected");
      return;
    }

    if (isEditDialogOpen && selectedProfile) {
      // Update existing profile
      setProfiles(
        profiles.map((p) =>
          p.id === selectedProfile.id
            ? {
                ...p,
                name: formData.name,
                description: formData.description,
                permissions: formData.permissions,
                updatedAt: new Date().toISOString(),
              }
            : p
        )
      );
      toast.success("User profile updated successfully");
      setIsEditDialogOpen(false);
    } else {
      // Create new profile
      const newProfile: UserProfile = {
        id: `profile-${Date.now()}`,
        name: formData.name,
        description: formData.description,
        permissions: formData.permissions,
        isDefault: false,
        isSystem: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setProfiles([...profiles, newProfile]);
      toast.success("User profile created successfully");
      setIsCreateDialogOpen(false);
    }

    setSelectedProfile(null);
    setFormData({ name: "", description: "", permissions: [] });
  };

  const togglePermission = (permission: PermissionKey) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const toggleCategoryPermissions = (category: string, checked: boolean) => {
    const categoryPermissions = ALL_PERMISSIONS.filter((p) => p.category === category).map((p) => p.key);

    setFormData((prev) => ({
      ...prev,
      permissions: checked
        ? [...new Set([...prev.permissions, ...categoryPermissions])]
        : prev.permissions.filter((p) => !categoryPermissions.includes(p)),
    }));
  };

  const isCategoryFullySelected = (category: string) => {
    const categoryPermissions = ALL_PERMISSIONS.filter((p) => p.category === category).map((p) => p.key);
    return categoryPermissions.every((p) => formData.permissions.includes(p));
  };

  const isCategoryPartiallySelected = (category: string) => {
    const categoryPermissions = ALL_PERMISSIONS.filter((p) => p.category === category).map((p) => p.key);
    return categoryPermissions.some((p) => formData.permissions.includes(p)) && !isCategoryFullySelected(category);
  };

  const ViewProfileDialog = ({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>View Profile: {selectedProfile?.name}</DialogTitle>
          <DialogDescription>
            {selectedProfile?.isSystem && (
              <Badge variant="outline" className="mt-2">System Profile</Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label>Profile Name</Label>
              <div className="p-3 bg-muted rounded-md">
                {selectedProfile?.name}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <div className="p-3 bg-muted rounded-md">
                {selectedProfile?.description}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Permissions ({selectedProfile?.permissions.length || 0} granted)</Label>
            <div className="border rounded-md">
              <Tabs defaultValue={PERMISSION_CATEGORIES[0].id} className="w-full">
                <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0 flex-wrap h-auto">
                  {PERMISSION_CATEGORIES.map((category) => (
                    <TabsTrigger
                      key={category.id}
                      value={category.id}
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary"
                    >
                      {category.label}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {PERMISSION_CATEGORIES.map((category) => {
                  const categoryPerms = ALL_PERMISSIONS.filter((p) => p.category === category.id);
                  const grantedPerms = categoryPerms.filter((p) => selectedProfile?.permissions.includes(p.key));

                  return (
                    <TabsContent key={category.id} value={category.id} className="p-4 space-y-4 max-h-[300px] overflow-y-auto">
                      <div className="flex items-center justify-between pb-2 border-b sticky top-0 bg-background z-10">
                        <Label className="font-semibold">
                          {category.label} Permissions
                        </Label>
                        <Badge variant="secondary">
                          {grantedPerms.length} of {categoryPerms.length}
                        </Badge>
                      </div>

                      <div className="space-y-3">
                        {categoryPerms.map((permission) => {
                          const isGranted = selectedProfile?.permissions.includes(permission.key);
                          return (
                            <div
                              key={permission.key}
                              className={`flex items-start space-x-2 p-3 rounded-md ${
                                isGranted ? "bg-green-50 dark:bg-green-950/20" : "bg-muted/50"
                              }`}
                            >
                              <div className="mt-0.5">
                                {isGranted ? (
                                  <div className="h-5 w-5 rounded-full bg-green-500 flex items-center justify-center">
                                    <svg
                                      className="h-3 w-3 text-white"
                                      fill="none"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                    >
                                      <path d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                ) : (
                                  <div className="h-5 w-5 rounded-full bg-muted border-2 border-muted-foreground/20" />
                                )}
                              </div>
                              <div className="grid gap-1 leading-none flex-1">
                                <Label className={`font-medium ${isGranted ? "text-green-700 dark:text-green-400" : "text-muted-foreground"}`}>
                                  {permission.label}
                                </Label>
                                <p className={`text-sm ${isGranted ? "text-green-600 dark:text-green-500" : "text-muted-foreground"}`}>
                                  {permission.description}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={() => {
            onOpenChange(false);
            if (selectedProfile) {
              handleDuplicate(selectedProfile);
            }
          }}>
            <Copy className="mr-2 h-4 w-4" />
            Duplicate Profile
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">User Profiles</h1>
          <p className="text-muted-foreground">Manage user profiles and permissions</p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Create Profile
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>All Profiles</CardTitle>
              <CardDescription>Configure access levels and permissions for different user roles</CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search profiles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Profile Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProfiles.map((profile) => (
                <TableRow key={profile.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4 text-muted-foreground" />
                      {profile.name}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-md">
                    <p className="text-sm text-muted-foreground truncate">{profile.description}</p>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{profile.permissions.length} permissions</Badge>
                  </TableCell>
                  <TableCell>
                    {profile.isSystem && <Badge variant="outline">System</Badge>}
                    {profile.isDefault && !profile.isSystem && <Badge>Default</Badge>}
                    {!profile.isSystem && !profile.isDefault && <Badge variant="secondary">Custom</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {profile.isSystem ? (
                        <Button variant="ghost" size="sm" onClick={() => handleView(profile)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(profile)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleDuplicate(profile)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      {!profile.isSystem && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(profile)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredProfiles.length === 0 && (
            <div className="text-center py-12">
              <Shield className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-4 text-lg font-semibold">No profiles found</h3>
              <p className="text-muted-foreground">Try adjusting your search query</p>
            </div>
          )}
        </CardContent>
      </Card>

      <ProfileDialog 
        open={isCreateDialogOpen} 
        onOpenChange={setIsCreateDialogOpen} 
        title="Create User Profile"
        formData={formData}
        setFormData={setFormData}
        onSave={saveProfile}
        togglePermission={togglePermission}
        toggleCategoryPermissions={toggleCategoryPermissions}
        isCategoryFullySelected={isCategoryFullySelected}
        isCategoryPartiallySelected={isCategoryPartiallySelected}
      />
      <ProfileDialog 
        open={isEditDialogOpen} 
        onOpenChange={setIsEditDialogOpen} 
        title="Edit User Profile"
        formData={formData}
        setFormData={setFormData}
        onSave={saveProfile}
        togglePermission={togglePermission}
        toggleCategoryPermissions={toggleCategoryPermissions}
        isCategoryFullySelected={isCategoryFullySelected}
        isCategoryPartiallySelected={isCategoryPartiallySelected}
      />
      <ViewProfileDialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen} />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User Profile</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedProfile?.name}"? This action cannot be undone. Users assigned to this profile will
              need to be reassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
